import axios from 'axios';

// Send the HttpOnly session cookie on every request
axios.defaults.withCredentials = true;

// Read the panel-issued JWT from either storage (localStorage preferred).
// Kept helper-shaped so BackToPanelButton + the axios interceptors + the
// module pages can share the same source of truth.
export const readBearerToken = () => {
  try {
    if (typeof window === 'undefined') return null;
    const local = window.localStorage && window.localStorage.getItem('talengers_bearer');
    if (local) return local;
    const sess = window.sessionStorage && window.sessionStorage.getItem('talengers_bearer');
    return sess || null;
  } catch { return null; }
};

export const clearBearerToken = () => {
  try {
    if (typeof window === 'undefined') return;
    if (window.localStorage) {
      window.localStorage.removeItem('talengers_bearer');
      window.localStorage.removeItem('talengers_panel_level');
    }
    if (window.sessionStorage) {
      window.sessionStorage.removeItem('talengers_bearer');
      window.sessionStorage.removeItem('talengers_panel_level');
    }
  } catch { /* ignore */ }
};

// Bearer-token fallback for browsers where corporate AV / extensions strip
// HttpOnly cookies from responses. When the panel login stores a token, we
// inject it as `Authorization: Bearer <token>` on every request to /api/*.
axios.interceptors.request.use((config) => {
  try {
    const url = String(config.url || '');
    if (url.startsWith('/api/') || url.includes('/api/')) {
      const tok = readBearerToken();
      if (tok) {
        config.headers = config.headers || {};
        if (!config.headers.Authorization) {
          config.headers.Authorization = `Bearer ${tok}`;
        }
      }
    }
  } catch { /* ignore */ }
  return config;
});

// Global 401 handler: any /api/* request that returns 401 while the user
// has a bearer token stored means the session expired. We wipe the token
// and bounce the tab back to /panel — the module's local login screen
// never shows in that case. Also handles the case where user closed the
// laptop overnight and comes back to a stale tab.
// Uses a lock to avoid re-triggering during the redirect navigation itself.
let _redirecting = false;
axios.interceptors.response.use(
  (r) => r,
  (err) => {
    try {
      const status = err?.response?.status;
      const url = String(err?.config?.url || '');
      const isApi = url.startsWith('/api/') || url.includes('/api/');
      // /api/panel/me is expected to return level=0 (via 200) even without a
      // session, so we don't gate on it. /api/panel/login is the login itself
      // — a 401 there means "wrong password", not "session expired".
      const isLoginProbe =
        url.includes('/api/panel/me') ||
        url.includes('/api/panel/login') ||
        url.includes('/api/panel/logout') ||
        url.includes('/api/auth/login') ||
        url.includes('/api/influs/session') ||
        url.includes('/api/commercial/login');
      if (status === 401 && isApi && !isLoginProbe && !_redirecting) {
        const path = (typeof window !== 'undefined' && window.location && window.location.pathname) || '';
        // Only auto-redirect from the internal modules, not the marketing site.
        // All internal modules now live under /panel/*.
        const internal = path.startsWith('/panel/');
        if (internal && readBearerToken()) {
          _redirecting = true;
          clearBearerToken();
          try {
            if (typeof document !== 'undefined') {
              document.cookie = 'panel_role_level=; Max-Age=0; path=/; SameSite=Lax';
            }
          } catch { /* ignore */ }
          window.location.href = '/panel';
        }
      }
    } catch { /* ignore */ }
    return Promise.reject(err);
  },
);

const API = '/api';

// Talentos
export const getTalentos = async () => {
  const response = await axios.get(`${API}/talentos`);
  return response.data.talentos;
};

// Admin-only: returns ALL talents including hidden ones (requires admin session)
export const getAllTalentosAdmin = async () => {
  const response = await axios.get(`${API}/talentos/all`);
  return response.data.talentos;
};

export const createTalento = async (talento) => {
  const response = await axios.post(`${API}/talentos`, talento);
  return response.data;
};

export const updateTalento = async (id, talento) => {
  const response = await axios.put(`${API}/talentos/${id}`, talento);
  return response.data;
};

export const deleteTalento = async (id) => {
  const response = await axios.delete(`${API}/talentos/${id}`);
  return response.data;
};

// Noticias
export const getNoticias = async () => {
  const response = await axios.get(`${API}/noticias`);
  return response.data.noticias;
};

// Contacto
export const sendContacto = async (contacto) => {
  const response = await axios.post(`${API}/contacto`, contacto);
  return response.data;
};

// Instagram Posts
export const getPosts = async () => {
  const response = await axios.get(`${API}/posts`);
  return response.data.posts;
};

export const createPost = async (post) => {
  const response = await axios.post(`${API}/posts`, post);
  return response.data;
};

export const updatePost = async (id, post) => {
  const response = await axios.put(`${API}/posts/${id}`, post);
  return response.data;
};

export const deletePost = async (id) => {
  const response = await axios.delete(`${API}/posts/${id}`);
  return response.data;
};

// Brands
export const getBrands = async () => {
  const response = await axios.get(`${API}/brands`);
  return response.data.brands;
};

export const createBrand = async (brand) => {
  const response = await axios.post(`${API}/brands`, brand);
  return response.data;
};

export const updateBrand = async (id, brand) => {
  const response = await axios.put(`${API}/brands/${id}`, brand);
  return response.data;
};

export const deleteBrand = async (id) => {
  const response = await axios.delete(`${API}/brands/${id}`);
  return response.data;
};

export const seedBrands = async () => {
  const response = await axios.post(`${API}/brands/seed`);
  return response.data;
};
